import {
    ComponentPropsWithoutRef,
    MouseEventHandler,
    useMemo,
    CSSProperties,
    useState,
} from 'react';

import { useIsAuthenticated } from 'hooks/api/auth/useIsAuthenticated';
import { useGallery } from 'hooks/store/useGallery';

import { StyledImage } from './Image.styled';

interface ImageStyle {
    thumbnail: CSSProperties;
    fullSize: CSSProperties;
}

interface ImageOnLoadType {
    handleImageOnLoad: () => void;
    css: ImageStyle;
}

export function useImageOnLoad(): ImageOnLoadType {
    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    const handleImageOnLoad = (): void => {
        setIsLoaded(true);
    };

    const css: ImageStyle = {
        thumbnail: {
            display: isLoaded ? 'none' : undefined,
            backgroundColor: `lightgray`,
            filter: 'blur(8px)',
        },
        fullSize: {
            display: isLoaded ? undefined : 'none',
        },
    };

    return { handleImageOnLoad, css };
}

export type ImageProps = ComponentPropsWithoutRef<'img'> & {
    modal?: boolean;
    srcUuid?: string;
    scale?: number;
    aspectRatio?: number;
    update_hash?: string;
};

export const Image = (props: ImageProps): JSX.Element => {
    const {
        alt,
        src = '',
        srcUuid,
        modal,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        children,
        scale = 100,
        aspectRatio,
        update_hash,
        ...rest
    } = props;

    const scaleFactor = scale / 100;

    const { authChecked, authenticated } = useIsAuthenticated();

    const imagePathPrefix =
        authChecked && authenticated ? '/api/builder/image' : '/api/image';

    const source = useMemo(() => {
        if (srcUuid) {
            return `${imagePathPrefix}/${srcUuid}/xs/3/?update_hash=${update_hash} 576w,
            ${imagePathPrefix}/${srcUuid}/sm/3/?update_hash=${update_hash} 768w,
            ${imagePathPrefix}/${srcUuid}/md/3/?update_hash=${update_hash} 992w,
            ${imagePathPrefix}/${srcUuid}/sm/3/?update_hash=${update_hash} 1200w,
            ${imagePathPrefix}/${srcUuid}/md/3/?update_hash=${update_hash} 1400w,`;
        }

        return src;
    }, [imagePathPrefix, src, update_hash, srcUuid]);

    const galleryHref = srcUuid
        ? `${imagePathPrefix}/${srcUuid}/original/1/`
        : source;

    const { handleImageOnLoad, css } = useImageOnLoad();

    const { setGallery } = useGallery();

    const preview = document.body.hasAttribute('data-preview');

    const onImageLinkClick: MouseEventHandler = (e) => {
        e.preventDefault();
        if (!preview) {
            setGallery({
                src: galleryHref,
                alt,
            });
        }
    };

    const maxWidth = 940 * scaleFactor;

    const image = (
        <div
            style={{
                maxWidth: `${maxWidth}px`,
                width: '100%',
            }}
            {...rest}
        >
            <div
                style={
                    aspectRatio
                        ? {
                              paddingTop: `${aspectRatio * 100}%`,
                              position: 'relative',
                          }
                        : undefined
                }
            >
                <StyledImage
                    alt="thumbnail"
                    style={css.thumbnail}
                    src={`${imagePathPrefix}/${srcUuid}/thumbnail/5/?update_hash=${update_hash}`}
                    data-aspect-ratio={!!aspectRatio}
                />
                <StyledImage
                    alt={alt || ''}
                    onLoad={handleImageOnLoad}
                    style={css.fullSize}
                    src={srcUuid ? undefined : source}
                    srcSet={srcUuid ? source : undefined}
                    data-aspect-ratio={!!aspectRatio}
                    onClick={modal ? (e) => onImageLinkClick(e) : undefined}
                />
            </div>
        </div>
    );

    return image;
};
